// src/pages/storePages/manageStore/ManageStoreLayout.jsx
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import Footer from "../../../components/common/Footer";
import "../../../static/storecss/manageStore/ManageStoreLayout.css";

const SECTIONS = [
  {
    id: "catalog",
    title: "Catalog",
    icon: "📦",
    items: [
      { to: "/manage-store", end: true, label: "Categories", icon: "🗂️" },
      { to: "/manage-store/products", label: "Products", icon: "📦" },
    ],
  },
  {
    id: "sales",
    title: "Sales",
    icon: "🛒",
    items: [{ to: "/manage-store/orders", label: "Orders", icon: "🧾" }],
  },
  {
    id: "settings",
    title: "Settings",
    icon: "⚙️",
    items: [{ to: "/manage-store/payment", label: "Payment info", icon: "💳" }],
  },
];

export default function ManageStoreLayout() {
  const { pathname } = useLocation();

  // Track open sections
  const [open, setOpen] = useState(() => {
    const match = SECTIONS.find((s) =>
      s.items.some((i) =>
        i.end ? pathname === i.to : pathname.startsWith(i.to)
      )
    );
    return match ? [match.id] : [SECTIONS[0].id];
  });

  // Mobile drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Keep the section with the active route open when the URL changes
  useEffect(() => {
    const match = SECTIONS.find((s) =>
      s.items.some((i) =>
        i.end ? pathname === i.to : pathname.startsWith(i.to)
      )
    );
    if (match) {
      setOpen((prev) =>
        prev.includes(match.id) ? prev : [...prev, match.id]
      );
    }
  }, [pathname]);

  // Escape closes the drawer
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIsDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggle = (id) =>
    setOpen((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleDrawer = () => setIsDrawerOpen((v) => !v);
  const closeDrawer = () => setIsDrawerOpen(false);

  // Which item label is active — shown in the mobile top bar
  const activeItem = SECTIONS.flatMap((s) => s.items).find((i) =>
    i.end ? pathname === i.to : pathname.startsWith(i.to)
  );

  return (
    <div className="h-a-p-ms-wrapper">
      {/* MOBILE TOP BAR */}
      <header className="h-a-p-ms-mobile-header">
        <button
          type="button"
          className="h-a-p-ms-drawer-toggle"
          onClick={toggleDrawer}
          aria-label="Toggle menu"
          aria-expanded={isDrawerOpen}
        >
          <span className="h-a-p-ms-hamburger-line" />
          <span className="h-a-p-ms-hamburger-line" />
          <span className="h-a-p-ms-hamburger-line" />
        </button>
        <span className="h-a-p-ms-mobile-title">
          {activeItem?.label || "Manage Store"}
        </span>
      </header>

      {/* BACKDROP */}
      <div
        className={`h-a-p-ms-drawer-backdrop ${
          isDrawerOpen ? "h-a-p-ms-drawer-backdrop-open" : ""
        }`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* CONTAINER */}
      <div className="h-a-p-ms-container">
        {/* SIDEBAR / DRAWER */}
        <aside
          className={`h-a-p-ms-sidebar ${
            isDrawerOpen ? "h-a-p-ms-sidebar-open" : ""
          }`}
        >
          <div className="h-a-p-ms-sidebar-inner">
            {/* Brand */}
            <Link
              to="/manage-store"
              className="h-a-p-ms-sidebar-brand"
              onClick={closeDrawer}
            >
              <span className="h-a-p-ms-sidebar-brand-mark">
                <i className="fa-solid fa-store" aria-hidden="true" />
              </span>
              <span className="h-a-p-ms-sidebar-brand-text">
                <strong>Manage Store</strong>
                <span>Hayducate</span>
              </span>
            </Link>

            {/* Close button (mobile only) */}
            <button
              type="button"
              className="h-a-p-ms-drawer-close"
              onClick={closeDrawer}
              aria-label="Close menu"
            >
              ✕
            </button>

            {/* Nav */}
            <nav className="h-a-p-ms-sidebar-nav">
              {SECTIONS.map((section) => {
                const isOpen = open.includes(section.id);

                return (
                  <div key={section.id} className="h-a-p-ms-section">
                    <button
                      type="button"
                      className={`h-a-p-ms-section-head ${
                        isOpen ? "h-a-p-ms-section-head-open" : ""
                      }`}
                      onClick={() => toggle(section.id)}
                      aria-expanded={isOpen}
                    >
                      <span className="h-a-p-ms-section-title">
                        <span className="h-a-p-ms-menu-item-icon">
                          {section.icon}
                        </span>
                        <span className="h-a-p-ms-menu-item-label">
                          {section.title}
                        </span>
                      </span>
                      <i
                        className={`fa-solid fa-chevron-right h-a-p-ms-section-chevron ${
                          isOpen ? "h-a-p-ms-section-chevron-open" : ""
                        }`}
                        aria-hidden="true"
                      />
                    </button>

                    {isOpen && (
                      <div className="h-a-p-ms-section-list">
                        {section.items.map((item) => (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            onClick={closeDrawer}
                            className={({ isActive }) =>
                              `h-a-p-ms-menu-item h-a-p-ms-menu-item-sub ${
                                isActive ? "h-a-p-ms-menu-item-active" : ""
                              }`
                            }
                          >
                            <span className="h-a-p-ms-menu-item-icon">
                              {item.icon}
                            </span>
                            <span className="h-a-p-ms-menu-item-label">
                              {item.label}
                            </span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            <div className="h-a-p-ms-sidebar-footer">
              <small className="h-a-p-ms-last-accessed">
                Last viewed: {activeItem?.label || "Overview"}
              </small>
            </div>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="h-a-p-ms-content">
          <Outlet />
        </main>
      </div>

      <Footer />
    </div>
  );
}